import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  CloudLightning, Zap, CheckCircle2, Loader2, User, Clock3, Radio, History
} from 'lucide-react';
import toast from 'react-hot-toast';
import { weatherAlertsAPI } from '../../services/api';
import PageHeader from '../../components/UI/PageHeader';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';

function fmtTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function fmtDuration(startIso, endIso) {
  const end = endIso ? new Date(endIso) : new Date();
  const s = Math.max(0, Math.floor((end - new Date(startIso)) / 1000));
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
}

function Countdown({ activeAlert }) {
  const [secsLeft, setSecsLeft] = useState(null);

  useEffect(() => {
    if (!activeAlert) return;
    const tick = () => {
      const lastAt = activeAlert.last_broadcast_at
        ? new Date(activeAlert.last_broadcast_at).getTime()
        : new Date(activeAlert.raised_at).getTime();
      const dueAt = lastAt + activeAlert.repeat_interval_sec * 1000;
      setSecsLeft(Math.max(0, Math.round((dueAt - Date.now()) / 1000)));
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [activeAlert]);

  if (secsLeft === null) return null;
  const m = Math.floor(secsLeft / 60);
  const s = secsLeft % 60;
  return (
    <span className="font-mono tabular-nums">
      {secsLeft === 0 ? 'broadcasting…' : `${m}:${String(s).padStart(2, '0')} to next repeat`}
    </span>
  );
}

export default function WeatherAlertsPage() {
  const [active, setActive] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [raising, setRaising] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [showRaiseForm, setShowRaiseForm] = useState(false);
  const [customIntervalMin, setCustomIntervalMin] = useState('');

  const load = useCallback(async () => {
    try {
      const [activeRes, historyRes] = await Promise.all([
        weatherAlertsAPI.getActive(),
        weatherAlertsAPI.getAll({ status: 'cleared' }),
      ]);
      setActive((activeRes.data.data || [])[0] || null);
      setHistory(historyRes.data.data || []);
    } catch {
      toast.error('Failed to load weather alerts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(load, 15000);
    return () => clearInterval(interval);
  }, [load]);

  async function handleRaise() {
    setRaising(true);
    try {
      const repeat_interval_sec = customIntervalMin ? Math.round(Number(customIntervalMin) * 60) : undefined;
      const res = await weatherAlertsAPI.raise(repeat_interval_sec ? { repeat_interval_sec } : {});
      toast.success(res.data.message || 'Lightning alert raised');
      setShowRaiseForm(false);
      setCustomIntervalMin('');
      load();
    } catch (err) {
      if (!err._toasted) toast.error('Failed to raise alert');
    } finally {
      setRaising(false);
    }
  }

  async function handleClear() {
    if (!active) return;
    if (!window.confirm('Clear the lightning warning alert? This stops the repeating broadcast.')) return;
    setClearing(true);
    try {
      await weatherAlertsAPI.clear(active.id);
      toast.success('Alert cleared');
      load();
    } catch (err) {
      if (!err._toasted) toast.error('Failed to clear alert');
    } finally {
      setClearing(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-4 border-blue-700 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-full bg-slate-50">
      <div className="px-6 py-6 max-w-4xl mx-auto space-y-6">
        <PageHeader
          icon={CloudLightning}
          title="Weather Alerts"
          subtitle="Lightning Warning Alert — baggage delivery notice"
        />

        {/* ── Active alert state ──────────────────────────────── */}
        {active ? (
          <div className="bg-red-600 rounded-2xl p-6 text-white shadow-lg shadow-red-600/20">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex items-start gap-3">
                <div className="w-11 h-11 rounded-xl bg-white/15 flex items-center justify-center flex-shrink-0">
                  <Zap size={20} />
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-red-100">Active</p>
                  <h2 className="text-xl font-black leading-tight">Lightning Warning Alert</h2>
                  <p className="text-sm text-red-100 mt-1">
                    Raised by {active.raisedByUser?.name || 'system'} · {fmtTime(active.raised_at)} · running {fmtDuration(active.raised_at)}
                  </p>
                </div>
              </div>
              <button
                onClick={handleClear}
                disabled={clearing}
                className="flex items-center gap-2 px-4 py-2.5 bg-white text-red-700 rounded-xl text-sm font-bold hover:bg-red-50 disabled:opacity-60 transition-colors flex-shrink-0"
              >
                {clearing ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />}
                Clear Alert
              </button>
            </div>

            <div className="mt-5 pt-4 border-t border-white/20 flex items-center gap-6 flex-wrap text-sm text-red-50">
              <span className="flex items-center gap-1.5">
                <Radio size={13} /> broadcast {active.broadcast_count}× so far
              </span>
              <span className="flex items-center gap-1.5">
                <Clock3 size={13} /> repeats every {Math.round(active.repeat_interval_sec / 60)}m
              </span>
              <span className="flex items-center gap-1.5">
                <Countdown activeAlert={active} />
              </span>
            </div>

            <p className="mt-4 text-xs text-red-100 bg-white/10 rounded-lg px-3 py-2">
              Clearing this alert stops the repeating broadcast. Per the CAG reference script, the
              formal "all clear" announcement (Msg 7) is marked ceased — no resumption message is
              announced automatically unless that's been re-enabled in config.
            </p>
          </div>
        ) : (
          <Card padded={false} className="p-6">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl bg-emerald-50 flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 size={20} className="text-emerald-600" />
                </div>
                <div>
                  <h2 className="text-base font-bold text-slate-900">No active weather alerts</h2>
                  <p className="text-sm text-slate-400">Raise one when a lightning warning affects baggage delivery.</p>
                </div>
              </div>
              <Button variant="danger" size="lg" icon={Zap} onClick={() => setShowRaiseForm(s => !s)} className="flex-shrink-0">
                Raise Lightning Alert
              </Button>
            </div>

            {showRaiseForm && (
              <div className="mt-5 pt-5 border-t border-slate-100">
                <label className="block text-xs font-semibold text-slate-500 mb-1.5">
                  Repeat interval (minutes) — optional, defaults to 10
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="number"
                    min="0.5"
                    step="0.5"
                    value={customIntervalMin}
                    onChange={e => setCustomIntervalMin(e.target.value)}
                    placeholder="10"
                    className="w-32 px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                  />
                  <Button variant="danger" icon={Zap} loading={raising} onClick={handleRaise}>
                    Confirm — Broadcast Now
                  </Button>
                </div>
                <p className="text-xs text-slate-400 mt-2">
                  Broadcasts immediately to all terminal zones, then repeats on this interval until cleared.
                </p>
              </div>
            )}
          </Card>
        )}

        {/* ── History ─────────────────────────────────────────── */}
        <div>
          <h3 className="text-sm font-bold text-slate-700 flex items-center gap-2 mb-3">
            <History size={15} className="text-slate-400" /> Past Alerts
          </h3>
          <Card padded={false} className="overflow-hidden">
            {history.length === 0 ? (
              <p className="px-4 py-8 text-center text-slate-400 text-sm">No cleared alerts yet.</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-100 text-left text-xs font-semibold text-slate-400 uppercase tracking-wide">
                    <th className="px-4 py-3">Raised</th>
                    <th className="px-4 py-3">Cleared</th>
                    <th className="px-4 py-3">Duration</th>
                    <th className="px-4 py-3">Broadcasts</th>
                    <th className="px-4 py-3">By</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map(h => (
                    <tr key={h.id} className="border-b border-slate-50 last:border-0">
                      <td className="px-4 py-3 text-slate-600">{fmtTime(h.raised_at)}</td>
                      <td className="px-4 py-3 text-slate-600">{fmtTime(h.cleared_at)}</td>
                      <td className="px-4 py-3 text-slate-600">{fmtDuration(h.raised_at, h.cleared_at)}</td>
                      <td className="px-4 py-3 text-slate-600">{h.broadcast_count}×</td>
                      <td className="px-4 py-3 text-slate-400 text-xs">
                        <span className="flex items-center gap-1"><User size={11} /> {h.raisedByUser?.name || '—'}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
